import { FC, useState, useCallback } from 'react';
import {
  Stack,
  Typography,
  CircularProgress,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Box,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useSchedulerRuleManager } from '../../../../../data-access';
import { BasicDialog } from '../../../../common';
import './AIAssistantMemorySettings.scss';

interface SchedulerRule {
  ruleId: string;
  content: string;
}

const AIAssistantMemorySettings: FC = () => {
  const { t } = useTranslation();
  const {
    schedulerRules,
    deleteSchedulerRule,
    createSchedulerRule,
    updateSchedulerRule,
    isLoadingRules,
  } = useSchedulerRuleManager();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [editingRule, setEditingRule] = useState<SchedulerRule | null>(null);
  const [ruleToDelete, setRuleToDelete] = useState<SchedulerRule | null>(null);
  const [memoryContent, setMemoryContent] = useState('');
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [newMemoryContent, setNewMemoryContent] = useState('');
  const [isSavingNew, setIsSavingNew] = useState(false);
  const [editingMemoryId, setEditingMemoryId] = useState<string | null>(null);
  const [editingMemoryContent, setEditingMemoryContent] = useState('');

  const handleOpenDeleteDialog = (rule: SchedulerRule) => {
    setRuleToDelete(rule);
    setIsDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!ruleToDelete) return;

    setIsLoading(true);
    try {
      await deleteSchedulerRule(ruleToDelete.ruleId);
      setIsDeleteDialogOpen(false);
      setRuleToDelete(null);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenModal = (rule: SchedulerRule) => {
    setEditingRule(rule);
    setMemoryContent(rule.content);
    setIsModalOpen(true);
  };

  const handleStartEditing = (rule: SchedulerRule) => {
    setEditingMemoryId(rule.ruleId);
    setEditingMemoryContent(rule.content);
    // Cancel any ongoing adding
    setIsAddingNew(false);
    setNewMemoryContent('');
  };

  const handleCancelEditing = () => {
    setEditingMemoryId(null);
    setEditingMemoryContent('');
  };

  const handleStartAddingNew = () => {
    setIsAddingNew(true);
    setNewMemoryContent('');
    // Cancel any ongoing editing
    setEditingMemoryId(null);
    setEditingMemoryContent('');
  };

  const handleCancelAddingNew = () => {
    setIsAddingNew(false);
    setNewMemoryContent('');
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingRule(null);
    setMemoryContent('');
  };

  const debouncedSaveNewMemory = useCallback(
    async (content: string) => {
      if (!content.trim()) return;

      setIsSavingNew(true);
      try {
        await createSchedulerRule({ content: content.trim() });
        setIsAddingNew(false);
        setNewMemoryContent('');
      } catch (error) {
        console.error('Failed to save memory:', error);
      } finally {
        setIsSavingNew(false);
      }
    },
    [createSchedulerRule]
  );

  const handleNewMemoryContentChange = (content: string) => {
    setNewMemoryContent(content);
  };

  const handleEditingMemoryContentChange = (content: string) => {
    setEditingMemoryContent(content);
  };

  const handleSaveEditedMemory = useCallback(async () => {
    if (!editingMemoryId || !editingMemoryContent.trim()) return;

    setIsSavingNew(true);
    try {
      await updateSchedulerRule({
        ruleId: editingMemoryId,
        content: editingMemoryContent.trim(),
      });
      setEditingMemoryId(null);
      setEditingMemoryContent('');
    } catch (error) {
      console.error('Failed to update memory:', error);
    } finally {
      setIsSavingNew(false);
    }
  }, [editingMemoryId, editingMemoryContent, updateSchedulerRule]);

  const handleSaveMemory = async () => {
    if (!memoryContent.trim()) return;

    setIsLoading(true);
    try {
      if (editingRule) {
        await updateSchedulerRule({
          ruleId: editingRule.ruleId,
          content: memoryContent,
        });
      } else {
        await createSchedulerRule({
          content: memoryContent,
        });
      }
      handleCloseModal();
    } finally {
      setIsLoading(false);
    }
  };

  const memoryPreview = ruleToDelete?.content
    ? ruleToDelete.content.length > 50
      ? `${ruleToDelete.content.substring(0, 50)}...`
      : ruleToDelete.content
    : '';

  return (
    <Stack className="ai-assistant-memory-settings">
      <Box className="ai-assistant-memory-settings__header">
        <Typography variant="body2" className="ai-assistant-memory-settings__title">
          {t('settings.aiAssistant.savedMemories')}
        </Typography>
        <Typography variant="subtitle2" className="ai-assistant-memory-settings__description">
          {t('settings.aiAssistant.savedMemoriesDescription')}
        </Typography>
      </Box>

      <Stack spacing={2}>
        {isLoadingRules ? (
          <Stack alignItems="center" justifyContent="center">
            <CircularProgress size={20} />
          </Stack>
        ) : schedulerRules.length === 0 ? (
          <Stack
            alignItems="center"
            justifyContent="center"
            spacing={1}
            className="ai-assistant-memory-settings__empty"
          >
            <Typography variant="body1" className="ai-assistant-memory-settings__empty-title">
              {t('settings.aiAssistant.noMemoriesYet')}
            </Typography>
            <Typography variant="body2" className="ai-assistant-memory-settings__empty-description">
              {t('settings.aiAssistant.noMemoriesDescription')}
            </Typography>
          </Stack>
        ) : (
          <Stack gap={1.5} className="ai-assistant-memory-settings__memories-container">
            {schedulerRules.map((rule, index) => (
              <Stack key={rule.ruleId}>
                {editingMemoryId === rule.ruleId ? (
                  <Box className="ai-assistant-memory-settings__new-memory-input">
                    <TextField
                      autoFocus
                      multiline
                      rows={2}
                      fullWidth
                      variant="outlined"
                      placeholder={t('settings.aiAssistant.memoryPlaceholder')}
                      value={editingMemoryContent}
                      onChange={(e) => handleEditingMemoryContentChange(e.target.value)}
                      disabled={isSavingNew}
                      InputProps={{
                        endAdornment: isSavingNew ? <CircularProgress size={16} /> : null,
                      }}
                      className="ai-assistant-memory-settings__new-memory-input__field"
                    />
                    <Stack direction="row" spacing={1} sx={{ mt: 1 }} justifyContent={'flex-end'}>
                      <Button size="small" onClick={handleCancelEditing} disabled={isSavingNew}>
                        {t('settings.common.cancel')}
                      </Button>
                      <Button
                        size="small"
                        variant="contained"
                        onClick={handleSaveEditedMemory}
                        disabled={isSavingNew || !editingMemoryContent.trim()}
                      >
                        {t('settings.common.save')}
                      </Button>
                    </Stack>
                  </Box>
                ) : (
                  <Stack direction="row" alignItems="flex-start" spacing={1.5}>
                    <Box flex={1}>
                      <Typography
                        variant="subtitle1"
                        className="ai-assistant-memory-settings__memory-text"
                      >
                        {rule.content}
                      </Typography>
                    </Box>
                    <Stack direction="row" spacing={0.5}>
                      <IconButton
                        size="small"
                        onClick={() => handleStartEditing(rule)}
                        className="ai-assistant-memory-settings__edit-button"
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDeleteDialog(rule)}
                        className="ai-assistant-memory-settings__delete-button"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  </Stack>
                )}
              </Stack>
            ))}

            {isAddingNew && (
              <Box className="ai-assistant-memory-settings__new-memory-input">
                <TextField
                  autoFocus
                  multiline
                  rows={2}
                  fullWidth
                  variant="outlined"
                  placeholder={t('settings.aiAssistant.memoryPlaceholder')}
                  value={newMemoryContent}
                  onChange={(e) => handleNewMemoryContentChange(e.target.value)}
                  disabled={isSavingNew}
                  InputProps={{
                    endAdornment: isSavingNew ? <CircularProgress size={16} /> : null,
                  }}
                  className="ai-assistant-memory-settings__new-memory-input__field"
                />
                <Stack direction="row" spacing={1} sx={{ mt: 1 }} justifyContent={'flex-end'}>
                  <Button size="small" onClick={handleCancelAddingNew} disabled={isSavingNew}>
                    {t('settings.common.cancel')}
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    onClick={() => debouncedSaveNewMemory(newMemoryContent)}
                    disabled={isSavingNew || !newMemoryContent.trim()}
                  >
                    {t('settings.common.save')}
                  </Button>
                </Stack>
              </Box>
            )}
          </Stack>
        )}
      </Stack>

      {!isAddingNew && (
        <button className="ai-assistant-memory-settings__add-button" onClick={handleStartAddingNew}>
          <AddIcon className="ai-assistant-memory-settings__add-button__icon" />
          <Typography variant="body2" className="ai-assistant-memory-settings__add-button__text">
            {t('settings.aiAssistant.addNewMemory')}
          </Typography>
        </button>
      )}

      <Dialog open={isModalOpen} onClose={handleCloseModal} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingRule ? t('settings.aiAssistant.editMemory') : t('settings.aiAssistant.addMemory')}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            multiline
            rows={4}
            fullWidth
            variant="outlined"
            label={t('settings.aiAssistant.memoryContent')}
            value={memoryContent}
            onChange={(e) => setMemoryContent(e.target.value)}
            placeholder={t('settings.aiAssistant.memoryPlaceholder')}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal}>{t('settings.common.cancel')}</Button>
          <LoadingButton
            onClick={handleSaveMemory}
            variant="contained"
            loading={isLoading}
            disabled={!memoryContent.trim()}
          >
            {t('settings.common.save')}
          </LoadingButton>
        </DialogActions>
      </Dialog>

      <BasicDialog
        visible={isDeleteDialogOpen}
        title={t('settings.aiAssistant.deleteMemory')}
        description={t('settings.aiAssistant.deleteMemoryDescription', { memory: memoryPreview })}
        ctaString={t('settings.common.delete')}
        isLoading={isLoading}
        onYes={handleConfirmDelete}
        onClose={() => setIsDeleteDialogOpen(false)}
      />
    </Stack>
  );
};

export default AIAssistantMemorySettings;
